/**
 * PlacesPage — Plaatsen (spec §8.5).
 *
 * Search across object/category/subzone/exact location/notes, the exact
 * 31-category filter chips, add/edit BottomSheet form with visible SaveBar,
 * signed-URL photo thumbnails, delete via ConfirmDestructiveAction, and fully
 * honest sync states (Op apparaat opgeslagen … Synchronisatie mislukt) with
 * device-only drafts and retry-on-reconnect.
 */
import { useState } from 'react'
import {
  Banner,
  Button,
  Card,
  Chip,
  ConfirmDestructiveAction,
  EmptyState,
  ErrorState,
  IconButton,
  Skeleton,
  StatusChip,
  SyncStatus,
  TextField,
  useToast,
} from '@/components/ui'
import { PlusIcon, TrashIcon } from '@/components/icons'
import { PageHeader } from '@/components/navigation'
import type { Place } from '@/types'
import { ALL_CATEGORY, PLACE_FILTER_CATEGORIES, prefillCategory } from './categories'
import { draftKeyFor, type PlaceDraft } from './drafts'
import { PlaceCard } from './PlaceCard'
import { PlaceFormSheet, type PlaceFormTarget } from './PlaceFormSheet'
import { searchPlaces } from './searchPlaces'
import { usePlaces } from './usePlaces'
import './places.css'

type FormSession = { target: PlaceFormTarget; key: number }

export default function PlacesPage() {
  const {
    places,
    status,
    error,
    syncStates,
    drafts,
    online,
    pendingWrites,
    refresh,
    savePlace,
    deletePlace,
    persistDraft,
    discardDraft,
    retryDraft,
  } = usePlaces()
  const { showToast } = useToast()

  const [query, setQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>(ALL_CATEGORY)
  const [formSession, setFormSession] = useState<FormSession | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Place | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [retryingDraftKey, setRetryingDraftKey] = useState<string | null>(null)

  const visiblePlaces = searchPlaces(places, query, categoryFilter)
  const draftEntries = Object.entries(drafts).filter(([key]) => {
    if (!formSession) return true
    const openKey =
      formSession.target.kind === 'edit'
        ? draftKeyFor(formSession.target.place.id)
        : formSession.target.kind === 'draft'
          ? draftKeyFor(formSession.target.draft.placeId)
          : draftKeyFor(null)
    return key !== openKey
  })

  const openNewForm = () => {
    setFormSession({
      target: { kind: 'new', prefillCategory: prefillCategory(categoryFilter) },
      key: Date.now(),
    })
  }

  const openEditForm = (place: Place) => {
    setFormSession({ target: { kind: 'edit', place }, key: Date.now() })
  }

  const openDraftForm = (draft: PlaceDraft) => {
    setFormSession({ target: { kind: 'draft', draft }, key: Date.now() })
  }

  const closeForm = () => setFormSession(null)

  const handleSaved = () => {
    closeForm()
    showToast({ message: 'Plaats opgeslagen in de cloud.', tone: 'success' })
  }

  const handleCancelForm = () => {
    // Explicit discard: remove the device-only draft for this form session.
    if (formSession) {
      const placeId =
        formSession.target.kind === 'edit'
          ? formSession.target.place.id
          : formSession.target.kind === 'draft'
            ? formSession.target.draft.placeId
            : null
      discardDraft(draftKeyFor(placeId))
    }
    closeForm()
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget || deleting) return
    setDeleting(true)
    const ok = await deletePlace(deleteTarget.id)
    setDeleting(false)
    if (ok) {
      showToast({ message: 'Plaats verwijderd.', tone: 'success' })
      setDeleteTarget(null)
    } else {
      showToast({ message: 'Verwijderen is mislukt. Probeer het opnieuw.', tone: 'error' })
    }
  }

  const handleRetryDraft = async (key: string) => {
    if (retryingDraftKey) return
    setRetryingDraftKey(key)
    const ok = await retryDraft(key)
    setRetryingDraftKey(null)
    showToast(
      ok
        ? { message: 'Concept gesynchroniseerd met de cloud.', tone: 'success' }
        : { message: 'Synchronisatie mislukt. Het concept blijft op dit apparaat bewaard.', tone: 'error' },
    )
  }

  const cloudUnavailable = status === 'error' && error?.code === 'SUPABASE_NOT_CONFIGURED'
  const filtersActive = query.trim().length > 0 || categoryFilter !== ALL_CATEGORY

  return (
    <div className="places-page">
      <PageHeader title="Plaatsen" subtitle="Waar liggen je spullen?" />

      {!online ? (
        <Banner tone="warning">
          Je bent offline. Wijzigingen worden op dit apparaat bewaard en gesynchroniseerd zodra je
          weer online bent.
        </Banner>
      ) : null}
      {pendingWrites > 0 ? (
        <Banner tone="info">
          {pendingWrites === 1
            ? '1 wijziging wacht op synchronisatie.'
            : `${pendingWrites} wijzigingen wachten op synchronisatie.`}
        </Banner>
      ) : null}

      {status === 'error' && error ? (
        <ErrorState
          title={cloudUnavailable ? 'Cloudverbinding niet ingesteld' : 'Plaatsen laden mislukt'}
          error={error}
          onRetry={error.retryable ? () => void refresh() : undefined}
        />
      ) : null}
      {cloudUnavailable ? (
        <Banner tone="info">
          Je kunt plaatsen opslaan op dit apparaat. Ze worden gesynchroniseerd zodra de
          cloudverbinding beschikbaar is.
        </Banner>
      ) : null}

      <div className="places-search">
        <TextField
          label="Zoeken"
          value={query}
          onChange={setQuery}
          placeholder="Zoek op voorwerp, categorie, subzone of notitie"
        />
      </div>

      <div className="places-chips" role="group" aria-label="Filter op categorie">
        {PLACE_FILTER_CATEGORIES.map((category) => (
          <Chip
            key={category}
            selected={category === categoryFilter}
            onClick={() => setCategoryFilter(category)}
          >
            {category}
          </Chip>
        ))}
      </div>

      <div className="places-add">
        <Button variant="primary" fullWidth onClick={openNewForm}>
          <PlusIcon size={18} />
          Plaats toevoegen
        </Button>
      </div>

      {status === 'loading' ? <Skeleton lines={4} /> : null}

      {draftEntries.length > 0 ? (
        <section className="places-drafts" aria-label="Concepten op dit apparaat">
          <h2 className="places-section-title">Concepten op dit apparaat</h2>
          {draftEntries.map(([key, draft]) => (
            <Card key={key} padding="sm" className="places-draft-card">
              <div className="places-draft-info">
                <div className="places-draft-heading">
                  <strong>{draft.objectLabel || 'Concept zonder titel'}</strong>
                  <StatusChip status="neutral">Alleen op dit apparaat</StatusChip>
                </div>
                <p className="places-draft-meta">
                  {draft.category}
                  {draft.subzone ? ` · ${draft.subzone}` : ''}
                </p>
                <SyncStatus state={draft.state} />
              </div>
              <div className="places-draft-actions">
                <Button variant="secondary" size="sm" onClick={() => openDraftForm(draft)}>
                  Verder bewerken
                </Button>
                {online && (draft.state === 'sync_failed' || draft.state === 'local_saved') ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleRetryDraft(key)}
                    disabled={retryingDraftKey === key}
                    loading={retryingDraftKey === key}
                  >
                    Nu synchroniseren
                  </Button>
                ) : null}
                <IconButton
                  aria-label={`Verwijder concept ${draft.objectLabel || 'zonder titel'}`}
                  variant="danger"
                  onClick={() => discardDraft(key)}
                >
                  <TrashIcon />
                </IconButton>
              </div>
            </Card>
          ))}
        </section>
      ) : null}

      {status === 'ready' && visiblePlaces.length === 0 ? (
        <EmptyState
          title={filtersActive ? 'Geen plaatsen gevonden' : 'Nog geen plaatsen'}
          body={
            filtersActive
              ? 'Pas je zoekopdracht of filter aan.'
              : 'Voeg je eerste plaats toe om te onthouden waar je spullen liggen.'
          }
          action={
            filtersActive ? undefined : (
              <Button variant="primary" onClick={openNewForm}>
                <PlusIcon size={18} />
                Plaats toevoegen
              </Button>
            )
          }
        />
      ) : null}

      {visiblePlaces.length > 0 ? (
        <div className="places-grid">
          {visiblePlaces.map((place) => (
            <PlaceCard
              key={place.id}
              place={place}
              syncState={syncStates[place.id] ?? 'cloud_saved'}
              onEdit={openEditForm}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      ) : null}

      {formSession ? (
        <PlaceFormSheet
          key={formSession.key}
          open
          target={formSession.target}
          onSave={savePlace}
          onSaved={handleSaved}
          onCancel={handleCancelForm}
          onPersistDraft={persistDraft}
        />
      ) : null}

      <ConfirmDestructiveAction
        open={deleteTarget !== null}
        title="Plaats verwijderen"
        body={
          deleteTarget
            ? `Weet je zeker dat je “${deleteTarget.objectLabel}” wilt verwijderen? De bijbehorende foto wordt ook verwijderd.`
            : undefined
        }
        confirmLabel={deleting ? 'Verwijderen…' : 'Verwijderen'}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
